import type { Variants, Transition } from 'framer-motion';

// Check for reduced motion preference
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// Standard durations
export const DURATION = {
  fast: 0.15,
  normal: 0.25,
  slow: 0.4,
  radar: 1.2,
} as const;

// Standard easings
export const EASING = {
  easeInOut: [0.4, 0, 0.2, 1] as const,
  easeOut: [0, 0, 0.2, 1] as const,
  easeOutCubic: [0.33, 1, 0.68, 1] as const,
  spring: { type: 'spring', stiffness: 300, damping: 30 } as const,
} as const;

// Fade in/out variant
export const fadeVariants: Variants = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
};

// Slide up variant (for route transitions, bottom sheets, etc.)
export const slideUpVariants: Variants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
};

// Scale pulse variant (for save/action confirmations)
export const pulseVariants: Variants = {
  initial: { scale: 1 },
  pulse: { scale: 1.05, transition: { duration: 0.15, repeat: 1, repeatType: 'reverse' } },
};

// Stagger children variant (for lists, mission cards)
export const staggerContainerVariants: Variants = {
  initial: {},
  animate: {
    transition: {
      staggerChildren: 0.05,
      delayChildren: 0.1,
    },
  },
};

export const staggerItemVariants: Variants = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
};

// Dismiss variant (swipe right and fade)
export const dismissVariants: Variants = {
  initial: { opacity: 1, x: 0 },
  dismiss: { opacity: 0, x: 100, transition: { duration: 0.2 } },
};

// Radar dimension animation (for Ikigai wheel)
export const radarFillVariants: Variants = {
  initial: { pathLength: 0, opacity: 0 },
  animate: { pathLength: 1, opacity: 1 },
};

// Default transition for route animations
export const defaultTransition: Transition = {
  duration: DURATION.normal,
  ease: EASING.easeInOut,
};

// Reduced motion variants (instant state changes)
export const reducedMotionVariants: Variants = {
  initial: { opacity: 1 },
  animate: { opacity: 1 },
  exit: { opacity: 1 },
};

// Helper to get variants based on motion preference
export function getVariants(standard: Variants, reduced?: Variants): Variants {
  if (prefersReducedMotion()) {
    return reduced ?? reducedMotionVariants;
  }
  return standard;
}

// Helper to get transition based on motion preference
export function getTransition(standard: Transition): Transition {
  if (prefersReducedMotion()) {
    return { duration: 0 };
  }
  return standard;
}
